import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Gift, Check, Star, ChevronDown } from 'lucide-react';

import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import subscriptionApi, { LifetimeDeal } from '../services/subscriptionApi';
import apiService from '../services/api';

interface Workspace {
  id: number;
  name: string;
  subscription_status: string;
  is_lifetime: boolean;
}

const RedeemLifetimeDeal: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { showError } = useToast();

  const [code, setCode] = useState(searchParams.get('code') || '');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [success, setSuccess] = useState(false);

  const [deal, setDeal] = useState<LifetimeDeal | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | ''>('');
  const [workspacesLoading, setWorkspacesLoading] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Guard against double auto-validation (React Strict Mode)
  const hasAutoValidated = useRef(false);

  // Stacking result
  const [stackingResult, setStackingResult] = useState<{
    codes_redeemed: number;
    total_named_users: number;
    total_public_view_users: number;
    current_tier: string;
  } | null>(null);

  useEffect(() => {
    loadWorkspaces();
  }, []);

  useEffect(() => {
    // Auto-validate if code is in URL on mount (with guard for Strict Mode)
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl && !deal && !hasAutoValidated.current) {
      hasAutoValidated.current = true;
      handleValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadWorkspaces = async () => {
    setWorkspacesLoading(true);
    try {
      const response = await apiService.getWorkspaces();
      // Allow stacking on lifetime workspaces too
      const eligible = (Array.isArray(response) ? response : response.workspaces || []).filter(
        (w: Workspace) =>
          ['none', 'trial', 'trial_expired', 'lifetime'].includes(w.subscription_status)
      );
      setWorkspaces(eligible);
      if (eligible.length === 1) {
        setSelectedWorkspaceId(eligible[0].id);
      }
    } catch (err) {
      console.error('Failed to load workspaces', err);
    } finally {
      setWorkspacesLoading(false);
    }
  };

  const handleValidate = async () => {
    if (!code.trim()) {
      showError('Please enter a deal code');
      return;
    }

    setValidating(true);
    setDeal(null);

    try {
      // Use the new stacking-aware validation endpoint
      const result = await subscriptionApi.validateLifetimeCode(code);

      if (result.valid && result.deal) {
        // Map new response shape to LifetimeDeal interface for component compatibility
        const mappedDeal: LifetimeDeal = {
          deal_code: result.deal.code,
          deal_name: result.deal.name,
          deal_source: result.deal.source,
          tier: result.deal.tier,
          base_named_users: result.deal.grants.named_users,
          base_public_view_users: result.deal.grants.public_view_users,
          features: result.deal.grants.features || {},
          is_valid: true,
        };
        setDeal(mappedDeal);
      } else {
        showError(result.error || 'Invalid code');
      }
    } catch (err: any) {
      showError(err.message || 'Failed to validate code');
    } finally {
      setValidating(false);
    }
  };

  const handleRedeem = async () => {
    if (!selectedWorkspaceId || !deal) {
      showError('Please select a workspace');
      return;
    }

    setLoading(true);

    try {
      // Use the new stacking API
      const result = await subscriptionApi.stackLifetimeCode(
        selectedWorkspaceId as number,
        code
      );
      setStackingResult(result.stacking);
      setSuccess(true);
    } catch (err: any) {
      showError(err.message || 'Failed to redeem code');
    } finally {
      setLoading(false);
    }
  };

  const selectedWorkspace = workspaces.find((w) => w.id === selectedWorkspaceId);

  // Styles
  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: 'var(--v2-surface)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    color: 'var(--v2-text)',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'var(--font-sans)',
  };

  const buttonPrimaryStyle: React.CSSProperties = {
    padding: '14px 24px',
    backgroundColor: 'var(--v2-primary)',
    color: '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  };

  const buttonSecondaryStyle: React.CSSProperties = {
    padding: '12px 20px',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
  };

  // Not logged in state
  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--v2-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--v2-border)',
          maxWidth: '480px',
          width: '100%',
          padding: '48px 40px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: 'var(--radius-xl)',
            background: 'linear-gradient(135deg, var(--v2-primary) 0%, #818cf8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
          }}>
            <Gift size={28} strokeWidth={1.5} color="#ffffff" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--v2-text)', margin: '0 0 8px 0' }}>
            Redeem Lifetime Deal
          </h1>
          <p style={{ fontSize: '0.9375rem', color: 'var(--v2-text-tertiary)', margin: '0 0 24px 0' }}>
            Please log in to redeem your lifetime deal code.
          </p>
          <button
onClick={() => navigate('/auth?returnTo=/redeem')}
            style={buttonPrimaryStyle}
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    const workspace = workspaces.find((w) => w.id === selectedWorkspaceId);
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: 'var(--v2-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        fontFamily: 'var(--font-sans)',
      }}>
        <div style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-xl)',
          boxShadow: 'var(--shadow-xl)',
          border: '1px solid var(--v2-border)',
          maxWidth: '480px',
          width: '100%',
          padding: '48px 40px',
          textAlign: 'center',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            backgroundColor: 'var(--v2-success-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px',
          }}>
            <Check size={32} strokeWidth={2} color="var(--v2-success)" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--v2-text)', margin: '0 0 8px 0' }}>
            Code Redeemed Successfully!
          </h1>
          <p style={{ fontSize: '0.9375rem', color: 'var(--v2-text-tertiary)', margin: '0 0 24px 0' }}>
            Your workspace <strong>{workspace?.name}</strong> now has lifetime access.
          </p>

          {stackingResult && (
            <div style={{
              backgroundColor: 'var(--v2-surface-alt)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-lg)',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'left',
            }}>
              <p style={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                color: 'var(--v2-text-muted)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                margin: '0 0 12px 0',
              }}>
                Your Lifetime Benefits
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Codes Redeemed</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>{stackingResult.codes_redeemed}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Monthly Active Users (MAU)</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>{stackingResult.total_named_users}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Public View Users</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>{stackingResult.total_public_view_users}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)' }}>Current Tier</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-primary)' }}>{stackingResult.current_tier}</span>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button
              onClick={() => {
                setSuccess(false);
                setCode('');
                setDeal(null);
                setStackingResult(null);
                hasAutoValidated.current = false;
                loadWorkspaces();
              }}
              style={buttonSecondaryStyle}
            >
              Redeem Another Code
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              style={buttonPrimaryStyle}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: 'var(--v2-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        fontFamily: 'var(--font-sans)',
      }}
      onClick={() => dropdownOpen && setDropdownOpen(false)}
    >
      <div style={{
        backgroundColor: 'var(--v2-surface)',
        borderRadius: 'var(--radius-xl)',
        boxShadow: 'var(--shadow-xl)',
        border: '1px solid var(--v2-border)',
        maxWidth: '520px',
        width: '100%',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '32px 32px 24px',
          textAlign: 'center',
          borderBottom: '1px solid var(--v2-border)',
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: 'var(--radius-lg)',
            background: 'linear-gradient(135deg, var(--v2-primary) 0%, #818cf8 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
          }}>
            <Gift size={24} strokeWidth={1.5} color="#ffffff" />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--v2-text)', margin: '0 0 8px 0' }}>
            Redeem Lifetime Deal
          </h1>
          <p style={{ fontSize: '0.9375rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
            Enter your code to unlock permanent access
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '24px 32px 32px' }}>
          {/* Step 1: Enter Code */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'var(--v2-primary)',
                color: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 700,
              }}>1</span>
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                Enter your deal code
              </span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type="text"
                placeholder="e.g., PRIME-XXXXXXXX"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setDeal(null);
                  hasAutoValidated.current = false;
                }}
                disabled={validating || loading}
                style={{
                  ...inputStyle,
                  flex: 1,
                  fontFamily: 'var(--font-mono)',
                  opacity: (validating || loading) ? 0.6 : 1,
                }}
              />
              <button
                onClick={handleValidate}
                disabled={validating || !code.trim()}
                style={{
                  ...buttonSecondaryStyle,
                  opacity: (validating || !code.trim()) ? 0.6 : 1,
                  cursor: (validating || !code.trim()) ? 'not-allowed' : 'pointer',
                }}
              >
                {validating ? 'Checking...' : 'Validate'}
              </button>
            </div>
          </div>

          {/* Deal Details */}
          {deal && (
            <>
              {/* Valid Code Card */}
              <div style={{
                backgroundColor: 'var(--v2-success-bg)',
                border: '1px solid #86efac',
                borderRadius: 'var(--radius-lg)',
                padding: '20px',
                marginBottom: '24px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <Check size={18} strokeWidth={2} color="var(--v2-success)" />
                  <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--v2-success-dark)' }}>
                    Valid Code!
                  </span>
                  <span style={{
                    backgroundColor: 'var(--v2-primary)',
                    color: '#ffffff',
                    padding: '2px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}>
                    <Star size={10} strokeWidth={2} />
		    {deal.deal_source === 'primeclub' ? 'Prime Club' : deal.deal_source}
                  </span>
                </div>

                <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'var(--v2-success-dark)', margin: '0 0 16px 0' }}>
                  {deal.deal_name}
                </h3>

                <div style={{ borderTop: '1px solid #86efac', paddingTop: '16px' }}>
                  <p style={{
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    color: 'var(--v2-success-dark)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    margin: '0 0 10px 0',
                  }}>
                    This code grants:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {[
                      `+${deal.base_named_users} Monthly Active  Users (MAU)`,
                      `+${deal.base_public_view_users} Public View Users`,
                      'Lifetime access (no recurring fees)',
                      'Stackable with other codes',
                    ].map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Check size={14} strokeWidth={2} color="var(--v2-success)" />
                        <span style={{ fontSize: '0.875rem', color: 'var(--v2-success-dark)' }}>{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Step 2: Select Workspace */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <span style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--v2-primary)',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                  }}>2</span>
                  <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)' }}>
                    Select workspace to apply deal
                  </span>
                </div>

                {workspacesLoading ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'var(--v2-text-tertiary)' }}>
                    Loading workspaces...
                  </div>
                ) : workspaces.length === 0 ? (
                  <div style={{
                    backgroundColor: 'var(--v2-warning-bg)',
                    border: '1px solid #fde68a',
                    borderRadius: 'var(--radius-md)',
                    padding: '14px 16px',
                  }}>
                    <p style={{ fontSize: '0.875rem', color: '#92400e', margin: 0 }}>
                      No eligible workspaces found. Lifetime codes can be applied to workspaces
                      without an active paid subscription, or stacked on existing lifetime workspaces.
                      <button
                        onClick={() => navigate('/dashboard')}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#92400e',
                          fontWeight: 600,
                          textDecoration: 'underline',
                          cursor: 'pointer',
                          marginLeft: '4px',
                          padding: 0,
                          fontSize: '0.875rem',
                        }}
                      >
                        Create Workspace
                      </button>
                    </p>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropdownOpen(!dropdownOpen);
                      }}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        backgroundColor: 'var(--v2-surface)',
                        border: `1px solid ${dropdownOpen ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
                        borderRadius: 'var(--radius-md)',
                        fontSize: '0.875rem',
                        color: selectedWorkspace ? 'var(--v2-text)' : 'var(--v2-text-muted)',
                        textAlign: 'left',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        opacity: loading ? 0.6 : 1,
                        boxShadow: dropdownOpen ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {selectedWorkspace ? (
                          <>
                            {selectedWorkspace.name}
                            <span style={{
                              backgroundColor: selectedWorkspace.subscription_status === 'lifetime' ? 'var(--v2-success-bg)' : 'var(--v2-bg)',
                              color: selectedWorkspace.subscription_status === 'lifetime' ? 'var(--v2-success-dark)' : 'var(--v2-text-tertiary)',
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                            }}>
                              {selectedWorkspace.subscription_status === 'lifetime' ? 'Lifetime (stack more)' : selectedWorkspace.subscription_status}
                            </span>
                          </>
                        ) : (
                          'Select a workspace'
                        )}
                      </span>
                      <ChevronDown size={16} strokeWidth={1.5} color="var(--v2-text-tertiary)" />
                    </button>

                    {dropdownOpen && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        backgroundColor: 'var(--v2-surface)',
                        border: '1px solid var(--v2-border)',
                        borderRadius: 'var(--radius-md)',
                        boxShadow: 'var(--shadow-lg)',
                        zIndex: 10,
                        maxHeight: '200px',
                        overflowY: 'auto',
                      }}>
                        {workspaces.map((w) => (
                          <button
                            key={w.id}
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedWorkspaceId(w.id);
                              setDropdownOpen(false);
                            }}
                            style={{
                              width: '100%',
                              padding: '12px 14px',
                              backgroundColor: selectedWorkspaceId === w.id ? 'var(--v2-surface-alt)' : 'transparent',
                              border: 'none',
                              textAlign: 'left',
                              cursor: 'pointer',
                              fontSize: '0.875rem',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            <span style={{ fontWeight: 500, color: 'var(--v2-text)' }}>{w.name}</span>
                            <span style={{
                              backgroundColor: w.subscription_status === 'lifetime' ? 'var(--v2-success-bg)' : 'var(--v2-bg)',
                              color: w.subscription_status === 'lifetime' ? 'var(--v2-success-dark)' : 'var(--v2-text-tertiary)',
                              padding: '2px 8px',
                              borderRadius: 'var(--radius-sm)',
                              fontSize: '0.6875rem',
                              fontWeight: 600,
                            }}>
                              {w.subscription_status === 'lifetime' ? 'Lifetime (stack more)' : w.subscription_status}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Step 3: Redeem Button */}
              <button
                onClick={handleRedeem}
                disabled={loading || !selectedWorkspaceId}
                style={{
                  ...buttonPrimaryStyle,
                  width: '100%',
                  opacity: (loading || !selectedWorkspaceId) ? 0.6 : 1,
                  cursor: (loading || !selectedWorkspaceId) ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? 'Redeeming...' : 'Redeem Now'}
              </button>
            </>
          )}

          {/* Footer */}
          <div style={{
            borderTop: '1px solid var(--v2-border)',
            marginTop: '24px',
            paddingTop: '20px',
            textAlign: 'center',
          }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: 0 }}>
              Don't have a code?{' '}
              <button
                onClick={() => navigate('/dashboard')}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--v2-primary)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '0.875rem',
                }}
              >
                Start a free trial instead
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedeemLifetimeDeal;
